import React, { useState, useEffect } from 'react';
import { Badge, Tooltip, Avatar, Box, Flex, Text, Spinner, useToast } from '@chakra-ui/react';
import axiosClient from '../../../../axios-client';
import './Style.css';

const TransporterBadges = ({ transporters, orderId }) => {
    const [expanded, setExpanded] = useState(false);
    const [localTransporters, setLocalTransporters] = useState(transporters || []);
    const [loadingIds, setLoadingIds] = useState({});
    const toast = useToast();

    useEffect(() => {
        setLocalTransporters(transporters || []);
    }, [transporters]);

    if (!localTransporters || localTransporters.length === 0) {
        return <Text color="gray.500" fontSize="sm">-</Text>;
    }

    const MAX_VISIBLE = 3;
    const hasMore = localTransporters.length > MAX_VISIBLE;
    const visibleTransporters = expanded ? localTransporters : localTransporters.slice(0, MAX_VISIBLE);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Envoyé': return 'green';
            case 'En attente': return 'yellow';
            case 'Échec': return 'red';
            default: return 'gray';
        }
    };

    const handleRetry = async (e, t) => {
        e.stopPropagation();

        // Only allow retry for 'Échec' or 'En attente'
        if (t.statut === 'Envoyé' || loadingIds[t.id]) return;

        setLoadingIds(prev => ({ ...prev, [t.id]: true }));
        try {
            const response = await axiosClient.post(`/commandes/${orderId}/send_to_shipping/`, {
                conn_id: t.conn_id
            });

            if (response.data.success) {
                toast({
                    title: 'Succès',
                    description: `Commande renvoyée à ${t.nom} avec succès.`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'bottom-right'
                });

                // Update local status
                setLocalTransporters(prev => prev.map(tr =>
                    tr.id === t.id ? { ...tr, statut: 'Envoyé' } : tr
                ));
            } else {
                toast({
                    title: 'Erreur',
                    description: response.data.message || `Échec de l'envoi à ${t.nom}.`,
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                    position: 'bottom-right'
                });

                // If it was 'En attente', it might become 'Échec'
                setLocalTransporters(prev => prev.map(tr =>
                    tr.id === t.id ? { ...tr, statut: 'Échec' } : tr
                ));
            }
        } catch (error) {
            console.error("Error retrying shipment:", error);
            toast({
                title: 'Erreur système',
                description: 'Une erreur est survenue lors de la tentative d\'envoi.',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setLoadingIds(prev => ({ ...prev, [t.id]: false }));
        }
    };

    const renderAvatarWithSpinner = (t, size) => {
        const isClickable = t.statut === 'Échec' || t.statut === 'En attente';
        const isLoading = loadingIds[t.id];

        return (
            <Box position="relative" cursor={isClickable ? "pointer" : "default"} onClick={(e) => isClickable && handleRetry(e, t)}>
                <Avatar
                    src={t.logo ? t.logo : undefined}
                    name={t.nom}
                    size={size}
                    borderWidth="1px"
                    borderColor="gray.200"
                    bg={t.logo ? "white" : undefined}
                    opacity={isLoading ? 0.5 : 1}
                />
                {isLoading && (
                    <Spinner
                        size={size === "xs" ? "xs" : "sm"}
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        color="blue.500"
                    />
                )}
            </Box>
        );
    };

    return (
        <Flex direction="column" align="start" gap={1}>
            <Flex
                direction={expanded ? 'column' : 'row'}
                align={expanded ? 'stretch' : 'center'}
                gap={2}
                wrap={expanded ? 'nowrap' : 'wrap'}
            >
                {visibleTransporters.map((t, index) => {
                    const isClickable = t.statut === 'Échec' || t.statut === 'En attente';
                    return (
                        <Tooltip
                            key={t.id || index}
                            placement="top"
                            hasArrow
                            label={
                                <Box>
                                    <Text fontWeight="bold">{t.nom}</Text>
                                    <Text>Statut: {t.statut}</Text>
                                    {isClickable && <Text fontSize="xs" color="blue.200">Cliquez pour renvoyer</Text>}
                                    {t.date && <Text fontSize="xs" mt={1}>{new Date(t.date).toLocaleString()}</Text>}
                                </Box>
                            }
                        >
                            {expanded ? (
                                <Flex
                                    align="center"
                                    gap={2}
                                    p={1}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    bg="gray.50"
                                    _dark={{ bg: "gray.700", borderColor: "gray.600" }}
                                    minW="150px"
                                    cursor={isClickable ? "pointer" : "default"}
                                    onClick={(e) => isClickable && handleRetry(e, t)}
                                    _hover={isClickable ? { bg: "gray.100", _dark: { bg: "gray.600" } } : {}}
                                >
                                    {renderAvatarWithSpinner(t, "xs")}
                                    <Flex direction="column" lineHeight="1" align="flex-start">
                                        <Text fontSize="xs" fontWeight="bold" noOfLines={1}>{t.nom}</Text>
                                        <Badge colorScheme={getStatusColor(t.statut)} fontSize="2xs" width="max-content">{t.statut}</Badge>
                                    </Flex>
                                </Flex>
                            ) : (
                                <Box position="relative">
                                    {renderAvatarWithSpinner(t, "sm")}
                                    <Box
                                        position="absolute"
                                        bottom="0"
                                        right="0"
                                        transform="translate(25%, 25%)"
                                        p="1"
                                        bg={`${getStatusColor(t.statut)}.500`}
                                        borderWidth="2px"
                                        borderColor="white"
                                        borderRadius="full"
                                        w="12px"
                                        h="12px"
                                        pointerEvents="none"
                                    >
                                        <Text as="span" srOnly>{t.statut}</Text>
                                    </Box>
                                </Box>
                            )}
                        </Tooltip>
                    );
                })}

                {hasMore && !expanded && (
                    <Flex
                        borderRadius="full"
                        align="center"
                        justify="center"
                        bg="gray.500"
                        color="white"
                        w="32px"
                        h="32px"
                        cursor="pointer"
                        fontSize="xs"
                        fontWeight="bold"
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(true);
                        }}
                    >
                        +{localTransporters.length - MAX_VISIBLE}
                    </Flex>
                )}
            </Flex>

            {hasMore && expanded && (
                <Text
                    color="blue.500"
                    mt={1}
                    fontSize="xs"
                    cursor="pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(false);
                    }}
                >
                    Voir moins
                </Text>
            )}
        </Flex>
    );
};

export default TransporterBadges;
